/**
 * Customer Forgot Password Action
 * Triggered by: arc:action:customerForgotPassword
 */

export const runCustomerForgotPassword = async (data = {}) => {
    try {
        const { 
            email,
            form,
            redirectOnSuccess = false,
            successMessage = 'Password reset instructions have been sent to your email.'
        } = data;

        if (!email) {
            throw new Error('Email address is required');
        }

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            throw new Error('Please enter a valid email address');
        }

        // Prepare form data
        const formData = new FormData();
        formData.append('form_type', 'recover_customer_password');
        formData.append('utf8', '✓');
        formData.append('email', email);

        // Dispatch reset attempt event
        window.dispatchEvent(new CustomEvent('customer:passwordResetAttempt', {
            detail: {
                email,
                timestamp: Date.now()
            }
        }));

        // Submit password reset request
        const response = await fetch('/account/recover', {
            method: 'POST',
            body: formData,
            headers: {
                'Accept': 'application/json, text/html',
            }
        });

        if (response.ok) {
            // Check if response is JSON (error) or HTML (success)
            const contentType = response.headers.get('content-type');
            
            if (contentType && contentType.includes('application/json')) {
                // JSON response usually means error
                const errorData = await response.json();
                throw new Error(errorData.message || 'Password reset failed');
            } else {
                // HTML response - check for success or error messages
                const responseText = await response.text();
                const parser = new DOMParser();
                const doc = parser.parseFromString(responseText, 'text/html');
                
                // Look for error messages
                const errorElement = doc.querySelector('.errors, .error, [data-error]');
                if (errorElement && errorElement.textContent.trim()) {
                    throw new Error(errorElement.textContent.trim());
                }
                
                // Success
                window.dispatchEvent(new CustomEvent('customer:passwordResetSuccess', {
                    detail: {
                        email,
                        message: successMessage,
                        timestamp: Date.now()
                    }
                }));

                if (redirectOnSuccess) {
                    // Redirect to login page with success message
                    const loginUrl = '/account/login?reset=success';
                    window.location.href = loginUrl;
                }

                return {
                    success: true,
                    email,
                    message: successMessage
                };
            }
        } else {
            // Handle HTTP errors
            let errorMessage = 'Password reset failed';
            
            try {
                const errorText = await response.text();
                const parser = new DOMParser();
                const doc = parser.parseFromString(errorText, 'text/html');
                const errorElement = doc.querySelector('.errors, .error, [data-error]');
                if (errorElement) {
                    errorMessage = errorElement.textContent.trim();
                }
            } catch (e) {
                console.error('Error parsing response:', e);
            }

            throw new Error(errorMessage);
        }

    } catch (error) {
        console.error('Customer Forgot Password error:', error);
        
        window.dispatchEvent(new CustomEvent('customer:passwordResetError', {
            detail: {
                error: error.message,
                email: data.email
            }
        }));

        return {
            success: false,
            error: error.message
        };
    }
};

// Initialize forgot password form listeners
export const initializeCustomerForgotPassword = (config = {}) => {
    const {
        formSelector = '[data-customer-forgot-password-form]',
        emailSelector = '[name="email"]',
        submitSelector = '[type="submit"]',
        autoSetupForms = true
    } = config;

    if (autoSetupForms) {
        setupForgotPasswordForms(formSelector, emailSelector, submitSelector);
    }

    window.dispatchEvent(new CustomEvent('customerForgotPassword:initialized', {
        detail: { config, timestamp: Date.now() }
    }));
};

// Setup forgot password form event listeners
const setupForgotPasswordForms = (formSelector, emailSelector, submitSelector) => {
    const forms = document.querySelectorAll(formSelector);
    
    forms.forEach(form => {
        form.removeEventListener('submit', handleForgotPasswordSubmit);
        form.addEventListener('submit', handleForgotPasswordSubmit);
    });
};

// Handle forgot password form submissions
const handleForgotPasswordSubmit = async (event) => {
    event.preventDefault();
    
    const form = event.target;
    const formData = new FormData(form);
    
    const email = formData.get('email');

    // Disable submit button
    const submitBtn = form.querySelector('[type="submit"]');
    const originalText = submitBtn?.textContent;
    if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Sending...';
    }

    try {
        await window.dispatchEvent(new CustomEvent('arc:action:customerForgotPassword', {
            detail: {
                email,
                form,
                redirectOnSuccess: false
            }
        }));
    } finally {
        // Re-enable submit button
        if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.textContent = originalText;
        }
    }
};

// Handle password reset form (when user clicks link in email)
export const initializePasswordResetForm = (config = {}) => {
    const {
        formSelector = '[data-customer-reset-password-form]',
        autoSetupForms = true
    } = config;

    if (autoSetupForms) {
        setupPasswordResetForms(formSelector);
    }
};

// Setup password reset form (from email link)
const setupPasswordResetForms = (formSelector) => {
    const forms = document.querySelectorAll(formSelector);
    
    forms.forEach(form => {
        form.removeEventListener('submit', handlePasswordResetSubmit);
        form.addEventListener('submit', handlePasswordResetSubmit);

        // Add password confirmation checking
        const confirmField = form.querySelector('[name="customer[password_confirmation]"]');
        if (confirmField) {
            confirmField.removeEventListener('input', handlePasswordConfirmation);
            confirmField.addEventListener('input', handlePasswordConfirmation);
        }
    });
};

// Handle password reset form submission
const handlePasswordResetSubmit = async (event) => {
    event.preventDefault();
    
    const form = event.target;
    const formData = new FormData(form);
    
    const password = formData.get('customer[password]');
    const passwordConfirmation = formData.get('customer[password_confirmation]');

    // Validation
    if (!password || !passwordConfirmation) {
        window.dispatchEvent(new CustomEvent('customer:passwordResetError', {
            detail: {
                error: 'Password and confirmation are required'
            }
        }));
        return;
    }

    if (password !== passwordConfirmation) {
        window.dispatchEvent(new CustomEvent('customer:passwordResetError', {
            detail: {
                error: 'Passwords do not match'
            }
        }));
        return;
    }

    if (password.length < 6) {
        window.dispatchEvent(new CustomEvent('customer:passwordResetError', {
            detail: {
                error: 'Password must be at least 6 characters long'
            }
        }));
        return;
    }

    // Disable submit button
    const submitBtn = form.querySelector('[type="submit"]');
    const originalText = submitBtn?.textContent;
    if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Updating password...';
    }

    try {
        // Submit the form normally as it contains the reset token
        form.submit();
    } finally {
        // Re-enable submit button (in case of error)
        if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.textContent = originalText;
        }
    }
};

// Password confirmation checking for reset form
const handlePasswordConfirmation = (event) => {
    const form = event.target.closest('form');
    const passwordField = form.querySelector('[name="customer[password]"]');
    const confirmPassword = event.target.value;
    const password = passwordField?.value || '';
    
    const matches = password === confirmPassword;
    
    window.dispatchEvent(new CustomEvent('customer:passwordConfirmationCheck', {
        detail: {
            matches,
            password,
            confirmPassword,
            context: 'reset'
        }
    }));
};

export default runCustomerForgotPassword;
